"use strict";
/**
 * The EclipseGG apps' version gate (platform spec §5, 2026-10-19).
 *
 * The native staff apps send their version in `X-GC-App-Version` on every
 * request. The seller sets ONE minimum for the whole platform in the admin
 * panel (`PUT /api/platform/settings`) — platform-wide, never per venue,
 * because an app build is the same build at every venue — and the server
 * answers `426 upgrade_required` to anything older, so a release that fixes
 * a money bug can be made the floor the day it ships.
 *
 * Three plain numbers, `MAJOR.MINOR.PATCH`, no leading zeros and no suffix:
 * App Store and Play versions are already this shape, and a format with
 * pre-release tags would need an ordering rule nobody here wants to own.
 * Each part is compared as a NUMBER (`1.10.0` is newer than `1.9.0`, which a
 * string comparison gets wrong).
 *
 * The browser staff website and the kiosk send no header and are never gated:
 * they are served by this same server, so they are always current.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.zAppVersion = exports.UPGRADE_REQUIRED_CODE = exports.APP_VERSION_HEADER = void 0;
exports.parseAppVersion = parseAppVersion;
exports.compareAppVersions = compareAppVersions;
const zod_1 = require("zod");
/** The request header the apps send. Lowercase: Node lowercases incoming header names. */
exports.APP_VERSION_HEADER = 'x-gc-app-version';
/** The refusal's `code`, with `details: { minimumVersion }` (HTTP 426). */
exports.UPGRADE_REQUIRED_CODE = 'upgrade_required';
/** Up to four digits a part is far beyond any real release, and bounds what a header can make us parse. */
const APP_VERSION_PATTERN = /^(0|[1-9]\d{0,3})\.(0|[1-9]\d{0,3})\.(0|[1-9]\d{0,3})$/;
/** A version the seller may set as the minimum. Trimmed; `1.4.0`, never `1.04.0` or `1.4`. */
exports.zAppVersion = zod_1.z
    .string()
    .trim()
    .regex(APP_VERSION_PATTERN, 'Use a version like 1.4.0: three numbers, no leading zeros.');
/** `[major, minor, patch]`, or null for anything that is not exactly three plain numbers. */
function parseAppVersion(raw) {
    const match = APP_VERSION_PATTERN.exec(raw.trim());
    if (!match)
        return null;
    return [Number(match[1]), Number(match[2]), Number(match[3])];
}
/**
 * -1 when `a` is older than `b`, 0 when equal, 1 when newer — part by part, as
 * numbers.
 *
 * A malformed version sorts BELOW every well-formed one (two malformed ones are
 * equal): a client sending a header it cannot spell is treated as too old,
 * never waved through as new (platform contract §3.15).
 */
function compareAppVersions(a, b) {
    const left = parseAppVersion(a);
    const right = parseAppVersion(b);
    if (!left || !right) {
        if (left)
            return 1;
        if (right)
            return -1;
        return 0;
    }
    for (let part = 0; part < 3; part += 1) {
        if (left[part] < right[part])
            return -1;
        if (left[part] > right[part])
            return 1;
    }
    return 0;
}
//# sourceMappingURL=app-version.js.map